import { NAVIGATION_HEADER_TITLES, NAVIGATION_PATHS } from "@/const/navigation";
import { isVercelPreviewHostname } from "./orgHostRouting";

const rootDomainEnv = process.env.NEXT_PUBLIC_ROOT_DOMAIN;
export const ROOT_DOMAIN =
  !rootDomainEnv || rootDomainEnv === "localhost"
    ? "localhost:3000"
    : rootDomainEnv;

export const LINK_DOMAIN =
  process.env.NEXT_PUBLIC_LINK_DOMAIN || "godetour.link";

// URLs rendered into HTML must pass an origin (see `requestOrigin.ts`). The
// `window` fallback gives different URLs on server and client.
export type UrlOrigin = { host: string; protocol: string };

function resolveOrigin(origin?: UrlOrigin): UrlOrigin | null {
  if (origin) return origin;
  if (typeof window === "undefined") return null;
  return { host: window.location.host, protocol: window.location.protocol };
}

function getBaseUrlForOrg(subdomain: string, origin?: UrlOrigin): string {
  const resolved = resolveOrigin(origin);
  if (!resolved) return "";

  const { host, protocol } = resolved;

  if (host.endsWith(ROOT_DOMAIN ?? "") && host !== ROOT_DOMAIN) {
    // Already on a subdomain host
    return `${protocol}//${host}`;
  }

  // On the main domain in production, route back to the org's subdomain host so
  // navigation away from the (main-domain-only) billing page returns the user
  // to their subdomain. In local dev (`localhost`) keep path-based since
  // browsers don't always resolve `acme.localhost` consistently.
  if (
    host === ROOT_DOMAIN &&
    !ROOT_DOMAIN.startsWith("localhost") &&
    subdomain
  ) {
    return `${protocol}//${subdomain}.${ROOT_DOMAIN}`;
  }

  // Fallback: path-based subdomain
  return `${protocol}//${host}/subdomain/${subdomain}`;
}

export function buildOrgUrl(
  subdomain: string,
  path: string,
  origin?: UrlOrigin
): string {
  const base = getBaseUrlForOrg(subdomain, origin);
  return `${base}${path}`;
}

/**
 * Returns true when running in a Vercel preview deployment that is NOT a
 * subdomain of the configured ROOT_DOMAIN (e.g. godetour.app).
 * Those deployments have no wildcard subdomain support, so all org navigation
 * must use path-based routing (/subdomain/<org>/) instead.
 */
export function isVercelPreviewDeploy(origin?: UrlOrigin): boolean {
  const resolved = resolveOrigin(origin);
  if (!resolved) return false;
  return isVercelPreviewHostname(resolved.host.split(":")[0]);
}

/**
 * Navigates to an org's root via a full browser navigation (window.location.href).
 *
 * Why window.location.href and not router.push/replace:
 * - router.* is same-origin only — cross-subdomain URLs are silently dropped or
 *   fall through to window.location.assign in ways that vary across Next.js versions.
 * - A full navigation is intentional here: it re-runs middleware, clears the
 *   Zustand store, and re-fetches org data fresh — exactly what you want after
 *   creating, deleting, or leaving an org.
 *
 * Why the localhost/preview branch:
 * - Wildcard subdomains don't resolve on localhost or Vercel preview deployments,
 *   so org routing falls back to path-based (/subdomain/<org>/).
 */
export function navigateToOrg(subdomain: string): void {
  if (
    (process.env.NODE_ENV !== "production" &&
      ROOT_DOMAIN?.includes("localhost")) ||
    isVercelPreviewDeploy()
  ) {
    window.location.href = `${window.location.origin}/subdomain/${subdomain}/`;
  } else {
    window.location.href = `${window.location.protocol}//${subdomain}.${ROOT_DOMAIN}/`;
  }
}

export function buildMainDomainUrl(path: string, origin?: UrlOrigin): string {
  const resolved = resolveOrigin(origin);

  // Vercel preview has no wildcard subdomain, so its host is the main domain.
  if (resolved && isVercelPreviewDeploy(resolved)) {
    return `${resolved.protocol}//${resolved.host}${path}`;
  }
  const protocol =
    resolved?.protocol ??
    (ROOT_DOMAIN.startsWith("localhost") ? "http:" : "https:");
  return `${protocol}//${ROOT_DOMAIN}${path}`;
}

/**
 * Builds the absolute, main-domain, path-based billing URL.
 *
 * Why: Paddle only verifies the main domain as a checkout origin. Org subdomains
 * are unverified, so the billing page must initialize Paddle on the main host.
 */
export function buildMainDomainBillingUrl(
  subdomain: string,
  origin?: UrlOrigin
): string {
  return buildMainDomainUrl(`/subdomain/${subdomain}/billing`, origin);
}

export function getNavigationTitle(pathname: string) {
  const normalizedPath = pathname.replace(
    /^\/subdomain\/[^/]+/,
    ""
  ) as NAVIGATION_PATHS;

  if (normalizedPath.includes(NAVIGATION_PATHS.Applications)) {
    return NAVIGATION_HEADER_TITLES[NAVIGATION_PATHS.Applications];
  }

  // Detail pages (an import job, a named event) are not in the map, and
  // falling through to "Dashboard" says the user left a section they are
  // still in.
  if (normalizedPath.startsWith("/analytics")) {
    return NAVIGATION_HEADER_TITLES[NAVIGATION_PATHS.AnalyticsOverview];
  }

  return NAVIGATION_HEADER_TITLES[normalizedPath] ?? "Dashboard";
}
